import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Plug, Plus, Trash2, Send, CheckCircle2, AlertTriangle,
  MessageSquare, Globe, Search, Bell, Webhook, Zap, Lock, ChevronRight, Copy, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';
import QuickstartStrip from '../components/integrations/QuickstartStrip';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ───────────────────────────────────────────────────────────────────
// Marketplace catalog — destinations the user can configure
// ───────────────────────────────────────────────────────────────────
const CATALOG = [
  {
    id: 'slack',
    kind: 'slack',
    name: 'Slack',
    desc: 'Send cost anomalies, weekly digests & budget alerts to a Slack channel via incoming webhook.',
    available: true,
    accent: 'from-emerald-500 to-emerald-600',
    placeholder: 'https://hooks.slack.com/services/T000.../B000.../...',
    helpUrl: 'https://api.slack.com/messaging/webhooks',
    helpText: 'Generate at api.slack.com/messaging/webhooks → Create New App → Incoming Webhooks.',
  },
  {
    id: 'teams',
    kind: 'teams',
    name: 'Microsoft Teams',
    desc: 'Push budget alerts and anomaly cards into a Teams channel using an incoming webhook connector.',
    available: true,
    accent: 'from-emerald-500 to-emerald-600',
    placeholder: 'https://*.webhook.office.com/webhookb2/...',
    helpUrl: 'https://learn.microsoft.com/en-us/microsoftteams/platform/webhooks-and-connectors/',
    helpText: 'In Teams: channel → Connectors → Incoming Webhook → Create.',
  },
  {
    id: 'generic',
    kind: 'generic',
    name: 'Generic Webhook',
    desc: 'Send HMAC-signed JSON to any HTTPS endpoint — wire up Jira, PagerDuty, Datadog, n8n, Zapier, your own service.',
    available: true,
    accent: 'from-emerald-500 to-emerald-600',
    placeholder: 'https://your-service.com/cartie-webhook',
    helpText: 'We sign every payload with HMAC-SHA256 in the X-Cartie-Signature header.',
  },
  {
    id: 'datadog',
    kind: 'generic',
    name: 'Datadog',
    desc: 'Forward cost anomalies & weekly digests as Datadog events. Use Datadog Webhooks integration to ingest HMAC-signed JSON.',
    available: true,
    accent: 'from-emerald-500 to-emerald-600',
    placeholder: 'https://api.datadoghq.com/api/v1/events?api_key=...',
    helpUrl: 'https://docs.datadoghq.com/integrations/webhooks/',
    helpText: 'In Datadog: Integrations → Webhooks → New. Paste your Datadog webhook URL here.',
  },
  {
    id: 'jira',
    kind: 'generic',
    name: 'Jira',
    desc: 'Auto-open cost-overrun tickets. Point CARTIE at your Jira Automation webhook trigger to create issues from anomalies.',
    available: true,
    accent: 'from-emerald-500 to-emerald-600',
    placeholder: 'https://automation.atlassian.com/pro/hooks/...',
    helpUrl: 'https://support.atlassian.com/cloud-automation/docs/jira-automation-triggers/#Incoming-webhook',
    helpText: 'In Jira: Project Settings → Automation → Create Rule → Incoming Webhook. Copy the URL here.',
  },
  {
    id: 'servicenow',
    kind: 'generic',
    name: 'ServiceNow',
    desc: 'Open ITSM change requests for over-budget projects. Wire CARTIE alerts into your ServiceNow Scripted REST endpoint.',
    available: true,
    accent: 'from-emerald-500 to-emerald-600',
    placeholder: 'https://<instance>.service-now.com/api/now/table/incident',
    helpUrl: 'https://docs.servicenow.com/bundle/utah-application-development/page/integrate/inbound-rest/concept/c_RESTAPI.html',
    helpText: 'In ServiceNow: System Web Services → Scripted REST APIs → create endpoint that accepts JSON.',
  },
  // Coming soon — keep visible to show roadmap
  { id: 'pagerduty', name: 'PagerDuty', desc: 'Escalate critical anomalies as incidents.', available: false, accent: 'from-emerald-500/70 to-emerald-600/70' },
  { id: 'discord', name: 'Discord', desc: 'Cost alerts in your engineering Discord.', available: false, accent: 'from-emerald-500/70 to-emerald-600/70' },
  { id: 'opsgenie', name: 'Opsgenie', desc: 'Route cost anomalies to on-call rotations.', available: false, accent: 'from-emerald-500/70 to-emerald-600/70' },
  { id: 'zapier', name: 'Zapier', desc: 'Trigger 5,000+ Zaps from any cost event.', available: false, accent: 'from-emerald-500/70 to-emerald-600/70' },
  { id: 'snowflake', name: 'Snowflake (Reverse-ETL)', desc: 'Stream FOCUS-formatted cost events back into your warehouse.', available: false, accent: 'from-emerald-500/70 to-emerald-600/70' },
];

const EVENT_LABELS = {
  anomaly: 'Cost anomaly detected',
  recommendation: 'New optimization recommendation',
  weekly_digest: 'Weekly cost digest',
  status_incident: 'CARTIEAI status incidents (platform-wide)',
  test: 'Test events only',
};

const KIND_BADGE = {
  slack: { label: 'Slack', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  teams: { label: 'Teams', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  generic: { label: 'Webhook', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
};

const IntegrationsMarketplace = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [search, setSearch] = useState('');
  const [installed, setInstalled] = useState([]);
  const [loading, setLoading] = useState(true);

  // Connect dialog state
  const [connectingTo, setConnectingTo] = useState(null);
  const [form, setForm] = useState({ name: '', url: '', events: ['anomaly', 'weekly_digest'] });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);

  useEffect(() => {
    fetchInstalled();
  }, []);

  const fetchInstalled = async () => {
    try {
      const res = await axios.get(`${API_URL}/integrations`, { headers });
      setInstalled(res.data || []);
    } catch (err) {
      console.error('Failed to load integrations', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = CATALOG.filter((d) =>
    d.name.toLowerCase().includes(search.toLowerCase()) ||
    d.desc.toLowerCase().includes(search.toLowerCase())
  );

  const openConnect = (dest) => {
    setForm({ name: `My ${dest.name}`, url: '', events: ['anomaly', 'weekly_digest', 'status_incident'] });
    setConnectingTo(dest);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.url.trim()) {
      toast.error('Name and URL are required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/integrations`,
        {
          name: form.name.trim(),
          kind: connectingTo.kind,
          url: form.url.trim(),
          enabled: true,
          events: form.events,
        },
        { headers }
      );
      toast.success(`${connectingTo.name} connected!`);
      setConnectingTo(null);
      fetchInstalled();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect — check the URL and try again');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this integration?')) return;
    try {
      await axios.delete(`${API_URL}/integrations/${id}`, { headers });
      toast.success('Integration removed');
      fetchInstalled();
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  const sendTest = async (id) => {
    setTesting(id);
    try {
      const res = await axios.post(`${API_URL}/integrations/test`, { integration_id: id }, { headers });
      if (res.data.success) {
        toast.success(`Test event delivered (HTTP ${res.data.status_code})`);
      } else {
        toast.error(`Delivery failed: HTTP ${res.data.status_code} — ${res.data.message?.slice(0, 80) || ''}`);
      }
      fetchInstalled();
    } catch (err) {
      toast.error('Test send failed');
    } finally {
      setTesting(null);
    }
  };

  const toggleEvent = (evt) => {
    setForm((f) => {
      const next = f.events.includes(evt) ? f.events.filter((e) => e !== evt) : [...f.events, evt];
      return { ...f, events: next };
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-8" data-testid="integrations-marketplace-page">
      {/* Hero */}
      <div className="rounded-2xl border border-emerald-500/15 bg-emerald-500/[0.06] p-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-xl bg-emerald-500">
            <Plug className="w-6 h-6 text-slate-950" />
          </div>
          <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-[11px] font-bold tracking-widest">
            INTEGRATIONS · FAN-OUT
          </Badge>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 dark:text-white"><BrandTitle>Connect CARTIEAI to where your team works</BrandTitle></h1>
        <p className="text-slate-400 dark:text-slate-400 mt-2 max-w-2xl">
          Stream every cost anomaly, weekly digest, and optimization recommendation into Slack, Teams,
          or any HTTPS endpoint with HMAC-signed payloads. Industry-standard, SSRF-protected, audit-logged.
        </p>
        <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-slate-400 dark:text-slate-400">
          <span className="flex items-center gap-1.5"><Lock className="w-4 h-4 text-emerald-500" /> SSRF-protected</span>
          <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> HMAC-SHA256 signed</span>
          <span className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-emerald-500" /> 6-second hard timeout</span>
        </div>
      </div>

      {/* Quickstart strip — auto-hides once user has 1+ integrations */}
      <QuickstartStrip catalog={CATALOG} installed={installed} onStart={openConnect} />

      {/* Installed integrations */}
      <Card data-testid="installed-integrations-card">
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><Webhook className="w-5 h-5 text-emerald-500" /> Your Integrations</CardTitle>
              <CardDescription>Configured destinations for cost events.</CardDescription>
            </div>
            <Badge variant="outline" className="text-xs">{installed.length} active</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-slate-400 py-6 text-center">Loading…</div>
          ) : installed.length === 0 ? (
            <div className="text-center py-10 text-slate-400 dark:text-slate-400">
              <div className="inline-flex p-4 rounded-2xl bg-slate-100 dark:bg-slate-800 mb-3">
                <Plug className="w-8 h-8 opacity-60" />
              </div>
              <p className="text-sm">No integrations configured yet — add one from the marketplace below.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {installed.map((it, idx) => (
                <div
                  key={it.id}
                  className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30"
                  data-testid={`installed-integration-${idx}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900 dark:text-white truncate">{it.name}</span>
                      <Badge variant="outline" className={`text-[11px] ${KIND_BADGE[it.kind]?.cls || ''}`}>
                        {KIND_BADGE[it.kind]?.label || it.kind}
                      </Badge>
                      {it.enabled ? (
                        <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-[11px]">Active</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[11px]">Paused</Badge>
                      )}
                    </div>
                    <div className="text-xs font-mono text-slate-400 truncate">{it.url_preview}</div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(it.events || []).map((e) => (
                        <span key={e} className="text-[11px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-400">{EVENT_LABELS[e] || e}</span>
                      ))}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-2">
                      {it.delivery_count > 0 ? `Delivered ${it.delivery_count} event${it.delivery_count === 1 ? '' : 's'}` : 'Not delivered yet'}
                      {it.last_delivered_at && ` · last ${new Date(it.last_delivered_at).toLocaleString()}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => sendTest(it.id)}
                      disabled={testing === it.id}
                      data-testid={`test-integration-${idx}`}
                    >
                      {testing === it.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      <span className="ml-1.5">Send Test</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(it.id)}
                      className="text-rose-600 hover:text-rose-700"
                      data-testid={`delete-integration-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Marketplace */}
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Marketplace</h2>
            <p className="text-sm text-slate-400 dark:text-slate-400">{CATALOG.length} destinations · {CATALOG.filter(c => c.available).length} live</p>
          </div>
          <div className="relative max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search destinations…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="marketplace-search"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((d) => (
            <Card
              key={d.id}
              className={`group relative overflow-hidden transition-all ${d.available ? 'hover:shadow-xl hover:-translate-y-0.5 cursor-pointer' : 'opacity-70'}`}
              data-testid={`marketplace-${d.id}`}
            >
              <div className={`absolute -top-12 -right-12 w-40 h-40 rounded-full bg-gradient-to-br ${d.accent} opacity-[0.07] group-hover:opacity-[0.12] transition-opacity blur-2xl`} />
              <CardContent className="p-5 relative">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${d.accent} flex items-center justify-center shadow-md`}>
                    {d.id === 'slack' || d.id === 'discord' || d.id === 'pagerduty' || d.id === 'opsgenie' ? (
                      <Bell className="w-5 h-5 text-white" />
                    ) : d.id === 'teams' ? (
                      <MessageSquare className="w-5 h-5 text-white" />
                    ) : d.id === 'generic' || d.id === 'zapier' ? (
                      <Webhook className="w-5 h-5 text-white" />
                    ) : (
                      <Globe className="w-5 h-5 text-white" />
                    )}
                  </div>
                  {d.available ? (
                    <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-[11px] font-bold tracking-wider">LIVE</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[11px] font-bold tracking-wider">SOON</Badge>
                  )}
                </div>
                <h3 className="font-bold text-slate-900 dark:text-white mb-1">{d.name}</h3>
                <p className="text-sm text-slate-400 dark:text-slate-400 mb-4 leading-relaxed line-clamp-3">{d.desc}</p>
                {d.available ? (
                  <Button
                    onClick={() => openConnect(d)}
                    className="w-full justify-between"
                    variant="outline"
                    data-testid={`connect-${d.id}-btn`}
                  >
                    <span className="flex items-center gap-2"><Plus className="w-4 h-4" /> Connect</span>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button disabled className="w-full justify-center" variant="outline">Coming soon</Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Connect dialog */}
      <Dialog open={!!connectingTo} onOpenChange={(o) => !o && setConnectingTo(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="connect-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plug className="w-5 h-5 text-emerald-500" />
              Connect {connectingTo?.name}
            </DialogTitle>
            <DialogDescription>{connectingTo?.helpText}</DialogDescription>
          </DialogHeader>
          {connectingTo && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="int-name">Display Name</Label>
                <Input
                  id="int-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={`e.g. #finance-alerts`}
                  data-testid="connect-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="int-url">Webhook URL</Label>
                <Input
                  id="int-url"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder={connectingTo.placeholder}
                  className="font-mono text-xs"
                  data-testid="connect-url-input"
                />
                {connectingTo.helpUrl && (
                  <a href={connectingTo.helpUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline">
                    How to get a {connectingTo.name} webhook URL →
                  </a>
                )}
              </div>
              <div className="space-y-2">
                <Label>Subscribe to events</Label>
                <div className="space-y-2">
                  {Object.entries(EVENT_LABELS).filter(([k]) => k !== 'test').map(([k, label]) => (
                    <div key={k} className="flex items-center justify-between p-2 rounded-lg border border-slate-200 dark:border-slate-700">
                      <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>
                      <Switch
                        checked={form.events.includes(k)}
                        onCheckedChange={() => toggleEvent(k)}
                        data-testid={`connect-event-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConnectingTo(null)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="connect-submit-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Connecting…</> : <><Plus className="w-4 h-4 mr-2" /> Connect</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrationsMarketplace;
